<script lang="ts" setup>
  import { useWindowScroll } from '@vueuse/core';
  import ScrollIndicator from '@/components/loggedOutNew/ScrollIndicator.vue';
  import SectionCafRaf from '@/components/loggedOutNew/SectionCafRaf.vue';
  import SectionDepositOptions from '@/components/loggedOutNew/SectionDepositOptions.vue';
  import SectionGameList from '@/components/loggedOutNew/SectionGameList.vue';
  import SectionHeroAndyRuiz from '@/components/loggedOutNew/SectionHeroAndyRuiz.vue';
  import SectionOriginalExclusive from '@/components/loggedOutNew/SectionOriginalExclusive.vue';

  import SectionRevShare from '@/components/loggedOutNew/SectionRevShare.vue';
  import SignUpBanner from '@/components/loggedOutNew/SignUpBanner.vue';

  const scrollIndicator = useTemplateRef('scrollIndicator');
  const showScrollIndicator = ref(true);
  const { y: windowScrollY } = useWindowScroll();

  watchOnce(windowScrollY, () => {
    const element = scrollIndicator.value?.$el;
    if (!element) return;
    element.classList.remove('opacity-100');
    element.classList.add('opacity-0');
  });

  // Refs for sections that need lazy loading
  const sectionOriginalExclusiveRef = ref<HTMLDivElement | null>(null);
  const sectionCafRafRef = ref<HTMLDivElement | null>(null);
  const sectionRevShareRef = ref<HTMLDivElement | null>(null);
  const sectionDepositOptionsRef = ref<HTMLDivElement | null>(null);

  // Visibility state for each section
  const isOriginalExclusiveVisible = ref(false);
  const isCafRafVisible = ref(false);
  const isRevShareVisible = ref(false);
  const isDepositOptionsVisible = ref(false);

  // Setup IntersectionObserver for each section
  useIntersectionObserver(
    sectionOriginalExclusiveRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !isOriginalExclusiveVisible.value) {
        isOriginalExclusiveVisible.value = true;
      }
    },
    { threshold: 0.1 },
  );

  useIntersectionObserver(
    sectionCafRafRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !isCafRafVisible.value) {
        isCafRafVisible.value = true;
      }
    },
    { threshold: 0.1 },
  );

  useIntersectionObserver(
    sectionRevShareRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !isRevShareVisible.value) {
        isRevShareVisible.value = true;
      }
    },
    { threshold: 0.1 },
  );

  useIntersectionObserver(
    sectionDepositOptionsRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting && !isDepositOptionsVisible.value) {
        isDepositOptionsVisible.value = true;
      }
    },
    { threshold: 0.1 },
  );
</script>

<template>
  <div
    class="max-w-screen lg:max-w-[67.875rem] desktop-1660:max-w-[90.5rem] mx-auto desktop-1660:mx-0 px-0 sm:px-3 md:px-4 lg:px-6 desktop-1660:px-0 flex flex-col gap-6 items-start justify-center"
  >
    <ScrollIndicator
      v-if="showScrollIndicator"
      ref="scrollIndicator"
      class="opacity-100 transition-all duration-500"
      @transitionend="showScrollIndicator = false"
    />

    <SignUpBanner @click="stores.modals.showSignUpModal" />
    <SectionHeroAndyRuiz @click="stores.modals.showSignUpModal" />
    <SectionGameList @click="stores.modals.showSignUpModal" />

    <!-- Lazy loaded sections with IntersectionObserver -->
    <div
      ref="sectionOriginalExclusiveRef"
      class="w-full lazy-section"
      :class="{ 'lazy-section-visible': isOriginalExclusiveVisible }"
    >
      <SectionOriginalExclusive v-if="isOriginalExclusiveVisible" @click="stores.modals.showSignUpModal" />
    </div>

    <div ref="sectionCafRafRef" class="w-full lazy-section" :class="{ 'lazy-section-visible': isCafRafVisible }">
      <SectionCafRaf v-if="isCafRafVisible" @click="stores.modals.showSignUpModal" />
    </div>

    <div ref="sectionRevShareRef" class="w-full lazy-section" :class="{ 'lazy-section-visible': isRevShareVisible }">
      <SectionRevShare v-if="isRevShareVisible" />
    </div>

    <div
      ref="sectionDepositOptionsRef"
      class="w-full mt-9 lazy-section"
      :class="{ 'lazy-section-visible': isDepositOptionsVisible }"
    >
      <SectionDepositOptions v-if="isDepositOptionsVisible" />
    </div>
  </div>
</template>
